import {
  ReportController,
  SectionController,
  PositionedModelController,
  InteractionEvent,
  NavigationAction,
  NavigationActionType,
  HiddenSection,
  PositionedModelOwnerController,
  BIRDModelController,
  VisualSelectionEvent,
} from '@sas-dvr/ltjs-bird';
import { ofActionCreatorType } from '../util/rxUtils';
import { from, merge, of, Observable, EMPTY } from 'rxjs';
import {
  filter,
  ignoreElements,
  map,
  mergeMap,
  tap,
  distinctUntilChanged,
  first,
  debounceTime,
  skip,
} from 'rxjs/operators';
import { BirdState } from '@sas-dvr/internal-va-react-core/store/bird/birdState';
import {
  SectionState,
  SectionData,
} from '@sas-dvr/internal-va-react-core/store/section/SectionState';
import { fromPortableEvent, getKey, withRetained } from '../util';
import { BIRDModelControllerLoader } from './LoaderFactory';
import { getControllerByName, getMediaContainerController } from '../util/controllerUtils';
import { DimensionUnit, StyleAttribute } from '@sas-dvr/ltjs-commons';
import { AnyAction } from 'redux';
import { loadHeaderController } from './loadPromptHeader';
import { ContainerState } from '@sas-dvr/internal-va-react-core/store/birdElement/containerState';
import { BirdLayoutState } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { TransportState } from '@sas-dvr/internal-va-react-core/store/transport/transportState';
import { getBackgroundColorString } from '@sas-dvr/ltjs-commons/util/StyleUtils';
import { EpicActions } from '../actions/epicActions';
import { ReportContextMenuState } from '@sas-dvr/internal-va-react-core/store/reportContextMenu/ReportContextMenuState';
import { ContextMenuItems } from '../util/contextMenu';
import { ExplanationState } from '@sas-dvr/internal-va-react-core/store/explanation/explanationState';
import { ReportPromptHeaderId } from '@sas-dvr/internal-va-react-core/components/PromptHeader/utils';
import { getTextStyles } from './promptElementImpl/utils';

function getInitialSectionInfo(controller: ReportController): SectionData[] {
  const sectionControllers = controller.getAllSectionControllers();
  if (!sectionControllers) {
    return [];
  }
  const sectionInfo: SectionData[] = [];
  for (const controller of sectionControllers) {
    if (!controller) {
      continue;
    }
    const info = getSectionInfo(controller);
    if (!info) {
      continue;
    }
    sectionInfo.push(info);
  }
  return sectionInfo;
}

function getSectionInfo(controller: SectionController): SectionData | undefined {
  const section = controller.getModel();
  const name = section?.getName();
  if (!section || !name) {
    return;
  }
  let padding: string | undefined;
  const paddingDimension = controller.getPagePadding();
  if (paddingDimension) {
    padding = paddingDimension.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0) + 'px';
  }
  return {
    id: name,
    label: section.getLabel() || '',
    padding,
  };
}

export const loadReportController: BIRDModelControllerLoader = (
  controller,
  unload$,
  myActions$,
  state$
) => {
  if (!(controller instanceof ReportController)) {
    throw new Error('Expected the controller to be a ReportController');
  }
  const objectName = state$.value.bird.name;

  let currentSectionController: SectionController | null = null;
  let previousSectionController: SectionController | null = null;
  let hiddenSectionController: SectionController | null = null;
  let nextSectionController: SectionController | null = null;
  const linkStack: { name: string; label: string }[] = [];

  const loadAndSetCurrentSection = (sectionController: SectionController | null) => {
    const actions: AnyAction[] = [];
    const viewSetReport = state$.value.bird.viewSetReport;

    if (nextSectionController) {
      nextSectionController.release();
      nextSectionController = null;
    }

    // Case for initial load
    if (currentSectionController === null) {
      currentSectionController = sectionController;
      if (currentSectionController) {
        currentSectionController.retain();
        actions.push(EpicActions.actions.loadBirdModel(currentSectionController));
        actions.push(
          SectionState.actions.setCurrentSection(
            currentSectionController.getModel()?.getName() ?? undefined
          )
        );
      }
    } else if (sectionController !== currentSectionController && !viewSetReport) {
      if (previousSectionController) {
        nextSectionController = sectionController;
        nextSectionController?.retain();
        return EMPTY;
      }
      previousSectionController = currentSectionController;
      currentSectionController = sectionController;
      if (currentSectionController) {
        currentSectionController.retain();
        actions.push(EpicActions.actions.loadBirdModel(currentSectionController));
        if (currentSectionController.isHiddenSection()) {
          const info = getSectionInfo(currentSectionController);
          const rootController = getMediaContainerController(sectionController);
          if (!info || !rootController) {
            throw new Error('Could not resolve hidden section info');
          }
          actions.push(
            SectionState.actions.updateSection(info.id, {
              ...info,
              rootContainerId: getKey(rootController),
              hidden: true,
            })
          );
          actions.push(SectionState.actions.setCurrentSection(info.id));
        } else {
          actions.push(
            SectionState.actions.setCurrentSection(
              currentSectionController?.getModel()?.getName() ?? undefined
            )
          );
        }
      }
    }
    return from(actions);
  };

  const setSectionLinkItem = (
    linkStack: { name: string; label: string }[],
    newStackPosition?: number
  ) => {
    const observables: Observable<AnyAction>[] = [];
    if (newStackPosition !== undefined) {
      const stepsToRemove = newStackPosition + 1;
      if (stepsToRemove <= 0 || stepsToRemove > linkStack.length) {
        return EMPTY;
      }
      linkStack.splice(0, stepsToRemove);
      // May trigger CURRENT_SECTION_CHANGED
      controller.removeLatestSectionLinkInfos(stepsToRemove);
      const sectionController = controller.getActiveSectionController();
      if (!sectionController) {
        throw new Error('getActiveSectionController should never be null');
      }
      observables.push(loadAndSetCurrentSection(sectionController));
    }

    const linkLabels = linkStack.map(({ label }) => label);
    return merge(
      ...observables,
      from([SectionState.actions.setLinkLabels(linkLabels.length ? linkLabels : undefined)])
    );
  };

  function cleanupSections() {
    const actions: AnyAction[] = [];
    for (const section of [
      currentSectionController,
      previousSectionController,
      hiddenSectionController,
      nextSectionController,
    ]) {
      if (!section) {
        continue;
      }
      section.release();
      actions.push(EpicActions.actions.unloadBirdModel(section));
    }
    return from(actions);
  }

  const observables$: Observable<AnyAction>[] = [];

  //
  // Load section data for all sections.
  //
  observables$.push(of(SectionState.actions.setSections(getInitialSectionInfo(controller))));

  //
  // Cleanup previous section when transition completes.
  //
  const cleanupPreviousSection$ = state$.pipe(
    map((state) => state.sections.transitioning),
    distinctUntilChanged(),
    mergeMap((transitioning) => {
      if (transitioning || !previousSectionController) {
        return EMPTY;
      }
      const unloadAction = EpicActions.actions.unloadBirdModel(previousSectionController);
      previousSectionController.release();
      previousSectionController = null;
      return of(unloadAction);
    })
  );

  //
  // Set current section on the controller when user changes section.
  //
  const handleNextSection$ = state$.pipe(
    map((state) => ({
      transitioning: state.sections.transitioning,
      nextSection: state.sections.nextSection,
    })),
    distinctUntilChanged(
      (next, current) =>
        next.transitioning === current.transitioning && next.nextSection === current.nextSection
    ),
    mergeMap((state) => {
      if (state.transitioning || state.nextSection === undefined) {
        return EMPTY;
      }
      const index = controller.getIndexOfSectionWithName(state.nextSection);
      controller.setCurrentSectionIndex(index);
      return of(SectionState.actions.setNextSection(undefined));
    })
  );

  const handleNextController$ = state$.pipe(
    map((state) => state.sections.transitioning),
    distinctUntilChanged(),
    mergeMap((transitioning) => {
      if (transitioning || nextSectionController === null) {
        return EMPTY;
      }
      return loadAndSetCurrentSection(nextSectionController);
    })
  );

  const styleChain = controller.getReportStyleChain();
  const backgroundColor = styleChain && getBackgroundColorString(styleChain);
  observables$.push(of(BirdState.actions.setReportBackgroundColor(backgroundColor || null)));

  const fontStyle = styleChain && getTextStyles(styleChain);
  observables$.push(of(BirdState.actions.setPageTabStyle(fontStyle || null)));

  const reportTheme = styleChain?.getStyleAttributeStringValue(StyleAttribute.THEME, null);
  // Always set a theme so TransportState.themeLoaded can be set to true
  observables$.push(of(BirdState.actions.setReportTheme(reportTheme ?? 'light')));
  // Clear theme in case of a reload
  observables$.push(unload$.pipe(map(() => BirdState.actions.setReportTheme(null))));

  // Notify the report controller when focus moves to a new visual
  observables$.push(
    state$.pipe(
      withRetained(controller),
      map((state) => state.bird.potentialFocusedVisual),
      // This state is driven by focus handlers that make rapid batches of updates.
      // These changes can be ignored because we only need the final value.
      debounceTime(0),
      distinctUntilChanged(),
      tap((newId) => {
        const selectedVisual = newId ? controller.getViewControllerForName(newId) : null;
        if (selectedVisual instanceof PositionedModelController) {
          controller.setSelectedVisual(selectedVisual);
        } else {
          controller.setSelectedVisual(null);
        }
      }),
      // Store the final value in redux so components can use it
      map((id) => BirdState.actions.setFocusedVisual(id))
    )
  );

  // Update the selected visual on SELECTION_CHANGED. This may be different from the focused visual.
  observables$.push(
    fromPortableEvent<VisualSelectionEvent>(
      controller,
      VisualSelectionEvent.SELECTION_CHANGED
    ).pipe(
      withRetained(controller),
      map((event) => {
        const selectedController = event.getVisualController();
        const id = selectedController ? getKey(selectedController) : null;
        return BirdState.actions.setSelectedVisual(id);
      })
    )
  );

  const handleNotifyLinkNavigation$ = myActions$.pipe(
    ofActionCreatorType<typeof SectionState.actions.notifyLinkNavigation>(
      SectionState.actionTypes.notifyLinkNavigation
    ),
    mergeMap((action) => {
      return setSectionLinkItem(linkStack, action.payload);
    })
  );

  //
  // When current section is changed, unload the old section, load the new section, and set the current section index.
  //
  const handleCurrentSectionChanged$ = fromPortableEvent(
    controller,
    ReportController.CURRENT_SECTION_CHANGED
  ).pipe(mergeMap(() => loadAndSetCurrentSection(controller.getCurrentSectionController())));

  const openExternalURL$ = fromPortableEvent<InteractionEvent>(
    controller,
    InteractionEvent.ACTION
  ).pipe(
    filter(isInteractionEvent(NavigationActionType.URL)),
    filter((e) => viewContainsEventSource(controller, objectName, e)),
    map((event) => event.getResolvedURL()),
    tap((url) => url && window.open(url, '_blank')),
    ignoreElements()
  );

  const followSectionLink$ = fromPortableEvent<InteractionEvent>(
    controller,
    InteractionEvent.ACTION
  ).pipe(
    filter(isInteractionEvent(NavigationActionType.SECTION)),
    mergeMap((event) => {
      const source = event.getSourceSection();
      if (!source) {
        return EMPTY;
      }

      const destController = controller.getActiveSectionController();
      // info window logic handled elsewhere
      if (!destController || destController.isInfoWindow()) {
        return EMPTY;
      }
      linkStack.unshift({
        name: source.getName() || '',
        label: source.getLabel() || '',
      });

      //
      // Force the section's link filters to be active.
      // This is needed to handle the case where the user had previously
      // turned off the page link incoming filters on that target section.
      // Now we force it back to be enabled on the next link to that page.
      //
      destController.setLinkFiltersActive(true);
      return merge(loadAndSetCurrentSection(destController), setSectionLinkItem(linkStack));
    })
  );

  const followHiddenSectionLink$ = fromPortableEvent<InteractionEvent>(
    controller,
    InteractionEvent.ACTION
  ).pipe(
    filter(isInteractionEvent(NavigationActionType.SECTION)),
    // Only the object that triggered the interaction should show the dialog
    filter((e) => viewContainsEventSource(controller, objectName, e)),
    mergeMap((event) => {
      const sectionName = event.getAction().getValue();
      if (!sectionName || !controller.isHiddenSection(sectionName)) {
        return EMPTY;
      }

      hiddenSectionController = controller.getHiddenSectionControllerForName(sectionName, event);
      if (!hiddenSectionController) {
        throw new Error(`Unable to find controller for hidden section named ${sectionName}`);
      }
      if (!hiddenSectionController.isInfoWindow()) {
        hiddenSectionController = null;
        return EMPTY;
      }
      hiddenSectionController.retain();

      const mediaContainerController = getMediaContainerController(hiddenSectionController);
      if (!mediaContainerController) {
        throw new Error(
          `Unable to find a media container controller for hidden section named ${sectionName}`
        );
      }

      const section = hiddenSectionController.getModel();
      if (!section || !(section instanceof HiddenSection)) {
        throw new Error(`No hidden section named ${sectionName}`);
      }
      const rootContainerId = getKey(mediaContainerController);

      const height =
        section
          .getHeight()
          ?.getScalarValueForUnit(DimensionUnit.PIXELS, 100, document.body.clientHeight) ||
        document.body.clientHeight * 0.5;

      const width =
        section
          .getWidth()
          ?.getScalarValueForUnit(DimensionUnit.PIXELS, 100, document.body.clientWidth) ||
        document.body.clientWidth * 0.5;

      let padding;
      const paddingDimension = hiddenSectionController.getPagePadding();
      if (paddingDimension) {
        padding = paddingDimension.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0) + 'px';
      }

      const sectionLabel = section.getLabel();

      const loadBirdModelAction = EpicActions.actions.loadBirdModel(hiddenSectionController);
      const showHiddenSectionAction = SectionState.actions.setHiddenSection({
        id: sectionName,
        rootContainerId,
        label: sectionLabel ?? undefined,
        size: { height, width },
        padding,
      });

      return from([loadBirdModelAction, showHiddenSectionAction]);
    })
  );

  const unloadHiddenSection$ = myActions$.pipe(
    ofActionCreatorType<typeof SectionState.actions.closeHiddenSection>(
      SectionState.actionTypes.closeHiddenSection
    ),
    mergeMap(() => {
      if (!hiddenSectionController) {
        return EMPTY;
      }
      return of(EpicActions.actions.unloadBirdModel(hiddenSectionController));
    }),
    tap(() => {
      if (hiddenSectionController) {
        controller.popHiddenSection();
        hiddenSectionController.release();
        hiddenSectionController = null;
      }
    })
  );

  // Cleanup when changing sections or closing popup section
  observables$.push(
    state$.pipe(
      map((state) => state.sections.hiddenSection ?? state.sections.currentSection),
      distinctUntilChanged(),
      skip(1),
      mergeMap(() =>
        from([
          // Maximized visual should be minimized
          BirdState.actions.setMaximizedVisual(null),
          // Pending explanations should be ignored
          ExplanationState.actions.reset(),
        ])
      )
    )
  );

  //
  // Load the initial section.
  //
  let handleContentComplete$: Observable<any> = EMPTY;
  if (objectName) {
    const sections = controller.getAllSectionControllers();
    if (!sections) {
      throw new Error(
        `Unable to find report object named ${objectName}. No sections exist in report.`
      );
    }

    const positionedModelController = controller.getViewControllerForName(objectName);
    if (positionedModelController instanceof SectionController) {
      const mediaContainerController = getMediaContainerController(positionedModelController);
      if (!mediaContainerController) {
        throw new Error(
          `Unable to find a media container controller for hidden section named ${objectName}`
        );
      }

      const section = positionedModelController.getModel();
      if (section instanceof HiddenSection) {
        const loadBirdModelAction = EpicActions.actions.loadBirdModel(positionedModelController);
        const rootContainerId = getKey(mediaContainerController);
        const setCurrentSectionAction = SectionState.actions.setCurrentSection(objectName);
        const updateSectionAction = SectionState.actions.updateSection(objectName, {
          id: objectName,
          rootContainerId,
          label: section.getLabel() || undefined,
        });

        observables$.push(
          from([loadBirdModelAction, setCurrentSectionAction, updateSectionAction]),
          unload$.pipe(map(() => EpicActions.actions.unloadBirdModel(positionedModelController)))
        );
      }
    } else if (positionedModelController instanceof PositionedModelController) {
      const mediaKey = `media-${getKey(positionedModelController)}`;
      observables$.push(
        from([
          EpicActions.actions.loadBirdModel(positionedModelController),
          ContainerState.actions.add(mediaKey, {}),
          BirdLayoutState.actions.set(mediaKey, {
            childKeys: [getKey(positionedModelController)],
          }),
        ])
      );
      observables$.push(unload$.pipe(map(() => ContainerState.actions.delete(mediaKey))));
      observables$.push(
        unload$.pipe(map(() => EpicActions.actions.unloadBirdModel(positionedModelController)))
      );
      if (positionedModelController instanceof PositionedModelOwnerController) {
        observables$.push(
          from([EpicActions.actions.addLayoutRoot(positionedModelController, mediaKey)])
        );
      } else {
        observables$.push(from([BirdLayoutState.actions.setIsSingleObject(mediaKey, true)]));
      }
      if (positionedModelController.isContentComplete()) {
        observables$.push(from([TransportState.actions.setReportObjectContentComplete(true)]));
      } else {
        handleContentComplete$ = fromPortableEvent(
          positionedModelController,
          BIRDModelController.CONTENT_COMPLETE
        ).pipe(
          first(),
          map(() => {
            return TransportState.actions.setReportObjectContentComplete(true);
          })
        );
      }
    } else {
      observables$.push(
        from([
          TransportState.actions.setTransportErrorKey({ key: 'ReportObject.notFound.error.txt' }),
        ])
      );
    }
  } else {
    if (controller.isContentComplete()) {
      observables$.push(from([TransportState.actions.setReportContentComplete(true)]));
    } else {
      handleContentComplete$ = fromPortableEvent(
        controller,
        BIRDModelController.CONTENT_COMPLETE
      ).pipe(
        first(() => controller.isContentComplete()),
        map(() => TransportState.actions.setReportContentComplete(true))
      );
    }
    observables$.push(loadAndSetCurrentSection(controller.getCurrentSectionController()));
  }

  //
  // Unload the current section when the report is unloaded.
  //
  const handleUnload$ = unload$.pipe(mergeMap(() => cleanupSections()));

  observables$.push(
    of(
      ReportContextMenuState.actions.setObjectItemSortKeys([
        ContextMenuItems.SORT,
        ContextMenuItems.EXPLAIN,
        ContextMenuItems.MAXIMIZE,
        ContextMenuItems.MANAGE_COLUMNS,
        ContextMenuItems.INCLUDE_MISSING,
      ])
    )
  );

  return merge(
    loadHeaderController(controller.getHeaderController(), unload$, ReportPromptHeaderId, state$),
    ...observables$,
    cleanupPreviousSection$,
    handleNextController$,
    handleNextSection$,
    handleNotifyLinkNavigation$,
    handleCurrentSectionChanged$,
    openExternalURL$,
    followSectionLink$,
    followHiddenSectionLink$,
    unloadHiddenSection$,
    handleUnload$,
    handleContentComplete$
  );
};

interface NavigationEvent extends InteractionEvent {
  getAction: () => NavigationAction;
}

function isInteractionEvent(type: NavigationActionType) {
  return function isTypedInteractionEvent(event: InteractionEvent): event is NavigationEvent {
    if (event.getType() !== InteractionEvent.ACTION) {
      return false;
    }
    const navigationAction = event.getAction();
    if (!(navigationAction instanceof NavigationAction)) {
      return false;
    }
    return navigationAction.getType() === type;
  };
}

function viewContainsEventSource(
  controller: ReportController,
  objectName: string | undefined,
  event: NavigationEvent
): boolean {
  if (!objectName) {
    return true;
  }
  let view = controller.getViewControllerForName(objectName);
  if (view instanceof SectionController) {
    view = getMediaContainerController(view);
  }
  const sourceName = event.getSourceController()?.getVisualElementName();
  return (
    objectName === sourceName ||
    (view instanceof PositionedModelOwnerController && !!getControllerByName(view, sourceName))
  );
}
